<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    width="760"
    @input="$emit('input', $event)"
  >
    <v-toolbar flat dense>
      <v-toolbar-title class="text-subtitle-1 font-weight-bold">
        {{ $t('classlink.reconcile.title') }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('input', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="px-4 pb-4">
      <p class="text-caption grey--text">
        {{ $t('classlink.reconcile.subtitle') }}
      </p>

      <v-alert v-if="source === 'persisted'" type="warning" dense text class="mb-3">
        {{ $t('classlink.reconcile.stale_source') }}
      </v-alert>

      <v-progress-linear v-if="loadingOrgs" indeterminate color="deep-purple" />

      <div v-if="!loadingOrgs && loadFailed" class="text-caption red--text">
        {{ $t('classlink.reconcile.load_error') }}
      </div>
      <div
        v-else-if="!loadingOrgs && orgs.length === 0"
        class="text-caption grey--text"
      >
        {{ $t('classlink.reconcile.all_linked') }}
      </div>

      <v-card
        v-for="org in orgs"
        :key="org.sourcedId"
        outlined
        class="mb-3"
      >
        <v-card-text>
          <div class="font-weight-medium">{{ org.name }}</div>
          <div class="text-caption grey--text mb-2">
            <code>{{ org.sourcedId }}</code>
          </div>

          <div v-if="org.candidates.length === 0" class="text-caption">
            {{ $t('classlink.reconcile.no_candidates') }}
          </div>

          <v-list v-else dense class="pa-0">
            <v-list-item
              v-for="candidate in org.candidates"
              :key="candidate.internalId"
              class="px-0"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ candidateText(candidate) }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  text
                  small
                  color="deep-purple"
                  :loading="isLinking(org, candidate)"
                  :disabled="isLinking(org, candidate)"
                  @click="link(org, candidate)"
                >
                  {{ $t('classlink.reconcile.link') }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </div>
  </v-navigation-drawer>
</template>

<script>
import rosterTenantsService from '@/services/roster-tenants.service';
import {
  apiErrorMessage,
  candidateLabel,
} from '@/utils/classlinkTenants';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'ClassLinkUnlinkedOrgsDrawer',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    tenant: { type: Object, default: null },
  },
  data: () => ({
    orgs: [],
    source: 'live',
    loadingOrgs: false,
    // Distingue "falló la carga" (le miente a CX si se confunde con "no hay
    // nada pendiente") de una lista vacía real.
    loadFailed: false,
    // Clave `${sourcedId}-${internalId}` de la vinculación en vuelo, para
    // deshabilitar sólo el botón que se apretó (mismo patrón que
    // `togglingIds` en la vista de conexiones).
    linkingKey: null,
  }),
  watch: {
    value(open) {
      if (open) this.loadOrgs();
    },
    // También por `tenant`: el drawer se abre desde cualquier fila, así que si
    // cambia de conexión mientras está abierto, `value` no cambia y sin esto
    // quedarían a la vista las orgs de la conexión anterior.
    tenant(next, prev) {
      if (this.value && next && (!prev || next.id !== prev.id)) this.loadOrgs();
    },
  },
  methods: {
    async loadOrgs() {
      if (!this.tenant) return;
      // Mismo guard que `ClassLinkSyncRunsDrawer`: si el drawer se reabre para
      // otra conexión antes de que esta request resuelva, la respuesta vieja
      // no debe pisar las orgs de la conexión nueva.
      const requestedId = this.tenant.id;
      this.loadingOrgs = true;
      this.orgs = [];
      this.source = 'live';
      this.loadFailed = false;
      try {
        const result = await rosterTenantsService.unlinkedOrgs(requestedId);
        if (this.tenant && this.tenant.id === requestedId) {
          this.orgs = result.orgs;
          this.source = result.source;
        }
      } catch (error) {
        if (this.tenant && this.tenant.id === requestedId) {
          this.orgs = [];
          this.loadFailed = true;
          this.snackbarError(apiErrorMessage(error, this.$t('classlink.reconcile.load_error')));
        }
      } finally {
        if (this.tenant && this.tenant.id === requestedId) {
          this.loadingOrgs = false;
        }
      }
    },
    // `candidateLabel` devuelve `{ key, params }`, nunca texto: hay que pasar
    // los dos a `$t`, nunca mostrar el objeto directo.
    candidateText(candidate) {
      const message = candidateLabel(candidate);
      return this.$t(message.key, message.params);
    },
    linkingKeyOf(org, candidate) {
      return `${org.sourcedId}-${candidate.internalId}`;
    },
    isLinking(org, candidate) {
      return this.linkingKey === this.linkingKeyOf(org, candidate);
    },
    async link(org, candidate) {
      if (!this.tenant) return;
      const key = this.linkingKeyOf(org, candidate);
      this.linkingKey = key;
      try {
        await rosterTenantsService.linkOrg(this.tenant.id, org.sourcedId, {
          // `org.type` es 'district' | 'school' tal como lo manda ClassLink,
          // y coincide exactamente con lo que valida el DTO del backend.
          internalType: org.type === 'district' ? 'district' : 'school',
          internalId: candidate.internalId,
        });
        this.snackbarSuccess(this.$t('classlink.reconcile.linked'));
        this.$emit('linked');
        await this.loadOrgs();
      } catch (error) {
        this.snackbarError(apiErrorMessage(error, this.$t('classlink.reconcile.link_error')));
      } finally {
        if (this.linkingKey === key) {
          this.linkingKey = null;
        }
      }
    },
  },
};
</script>
